import { useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { useGlobalContext } from '../../../../context/ContextGlobal'
import { getDocuments, getSubDocuments } from '../../../../firebase/firestore'
import { TryCatch } from '../../../../utils/general'
import { ConvertToTimestamp } from '../../../../utils/dates'

export const useClientEventReports = () => {
  const {
    user,
    setAlerts,
    loading,
    setLoading,
    dataContext,
    setDataContext, // contexto que guarda dados de lista para a pagina
    // infosContext,
    // setInfosContext, // contexto que guarda dados adicionais para a pagina funcionar
    formContext,
    // setFormContext, // contexto que guarda dados de formularios da pagina
  } = useGlobalContext()

  const { id } = useParams()

  const [filters, setFilters] = useState({
    date: 'todos',
    round: 'todos',
    positionName: 'todos',
  })

  const getData = async () => {
    // Busca tudo em paralelo
    const [staffList, jobOffersList, positionsList] = await Promise.all([
      getSubDocuments('events', id, 'staff'),
      getSubDocuments('events', id, 'jobOffers'),
      getDocuments('cargos'),
    ])

    // Transforma listas em maps para lookup O(1)
    const jobOffersMap = Object.fromEntries(jobOffersList.map((j) => [j.id, j]))
    const positionsMap = Object.fromEntries(positionsList.map((p) => [p.id, p]))

    const jobOffersEnriched = jobOffersList.map((job) => {
      const position = positionsList.find((pos) => pos.id === job.idPosition)

      return { ...job, position }
    })

    // Enriquecimento rápido
    const ListStaffersEnriched = staffList.map((staff) => {
      const jobOffer = jobOffersMap[staff.idJobOffers]
      const position = jobOffer ? positionsMap[jobOffer.idPosition] : null
      return { ...staff, jobOffer, position }
    })

    setDataContext((prev) => ({ ...prev, data: ListStaffersEnriched, jobOffers: jobOffersEnriched }))
  }

  const candidacyHistory = useMemo(() => {
    const candidacyHistoryObj = (dataContext?.data || []).reduce((acc, curr) => {
      const date = String(curr.dt_created).split(' ')[0]
      acc[date] = (acc[date] || 0) + 1
      return acc
    }, {})

    return Object.entries(candidacyHistoryObj)
      .sort(([a], [b]) => ConvertToTimestamp(a) - ConvertToTimestamp(b))
      .map(([date, count]) => ({
        date,
        staff: count,
      }))
  }, [dataContext?.data])

  const statusStaff = {
    em_aprovacao: [0],
    candidatura_recusada: [1],
    aguardando_confirmacao: [2],
    presenca_confirmada: [3],
    presenca_recusada: [4],
    checkins_realizados: [5],
    faltas_no_evento: [6],
    checkouts_realizados: [7],
    checkouts_nao_realizados: [8],
    pagamentos_pendentes: [9],
    pagamentos_realizados: [10],
    cancelados: [11],
    desistencias: [12],
    cancelados_recusas_desistencias_totais: [11, 12, 4, 1],
    checkins_realizados_totais: [5, 7, 9, 10],
    checkouts_realizados_totais: [7, 9, 10],
    total_staff: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    total_staff_participantes: [3, 5, 7, 8, 9, 10],
  }

  const colorsStatus = {
    em_aprovacao: '#646464',
    candidatura_recusada: '#ff5252',
    aguardando_confirmacao: '#9e9e9e',
    presenca_confirmada: '#4CAF50',
    presenca_recusada: '#ffa000',
    checkins_realizados: '#4CAF50',
    faltas_no_evento: '#ff9800',
    checkouts_realizados: '#2196F3',
    checkouts_nao_realizados: '#1976d2',
    pagamentos_pendentes: '#ffb300',
    pagamentos_realizados: '#00bfa5',
    cancelados: '#f44336',
    desistencias: '#e91e63',
  }

  const getStatusKey = (value) => {
    const entry = Object.entries(statusStaff).find(([key, arr]) => arr.includes(value))
    return entry ? entry[0] : 'status_undefined'
  }

  const reportStatusPerJobOfferData = (dataContext?.data || [])
    .map((curr) => {
      const date = curr.jobOffer?.date || 'date undefined'
      const positionName = curr.position?.nome || curr.position?.name || 'position name undefined'
      const round = curr.jobOffer?.round || 'round undefined'
      const status = curr.status
      const statusName = getStatusKey(curr.status)

      return { date, positionName, round, status, statusName }
    })
    .filter((item) => {
      const round = filters.round
      if (round === undefined || round === '' || round === null || round === 'todos') return true
      return item.round === round
    })
    .filter((item) => {
      const date = filters.date
      if (date === undefined || date === '' || date === null || date === 'todos') return true
      return item.date === date
    })
    .filter((item) => {
      const positionName = filters.positionName
      if (positionName === undefined || positionName === '' || positionName === null || positionName === 'todos')
        return true
      return item.positionName === positionName
    })

  const initialAcc = Object.keys(statusStaff).reduce((acc, key) => {
    acc[key] = 0
    return acc
  }, {})

  const reportStatusPerJobOfferObj = reportStatusPerJobOfferData.reduce((acc, curr) => {
    const positionName = curr.positionName
    const status = curr.status

    // inicializa o objeto da posição se não existir
    if (!acc[positionName]) acc[positionName] = { ...initialAcc }

    // percorre todas as categorias do statusStaff
    Object.entries(statusStaff).forEach(([statusName, statusArray]) => {
      if (statusArray.includes(status)) {
        acc[positionName][statusName] = (acc[positionName][statusName] || 0) + 1
      }
    })

    return acc
  }, {})

  const reportStatusPerJobOffer = Object.entries(reportStatusPerJobOfferObj)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map((item) => ({
      jobOffer: item[0],
      ...item[1],
    }))

  const reportStatusPerJobOfferTotal = reportStatusPerJobOffer.reduce((acc, obj) => {
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'number') {
        acc[key] = (acc[key] || 0) + value
      }
    }
    return acc
  }, {})

  const uniqueDates = [...new Set((dataContext.jobOffers || []).map((job) => job.date))]
  const dates = [{ value: 'todos', label: 'Todos' }, ...uniqueDates.map((v) => ({ value: v, label: v }))]

  const jobOfferFilteredDate = (dataContext.jobOffers || []).filter(
    (job) => job.date === filters.date || filters.date === 'todos',
  )

  const uniqueRounds = [...new Set(jobOfferFilteredDate.map((job) => job.round))]
  const rounds = [{ value: 'todos', label: 'Todos' }, ...uniqueRounds.map((v) => ({ value: v, label: v }))]

  const jobOfferFilteredRound = jobOfferFilteredDate.filter(
    (job) => job.round === filters.round || filters.round === 'todos',
  )

  const uniqueJobs = [...new Set(jobOfferFilteredRound.map((job) => job?.position?.nome || job?.position?.name))]
  const jobs = [{ value: 'todos', label: 'Todos' }, ...uniqueJobs.map((v) => ({ value: v, label: v }))]

  const FetchGetData = async () => {
    await TryCatch(getData, setLoading, setAlerts)
  }

  useEffect(() => {
    FetchGetData()
  }, [])

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      round: 'todos',
      positionName: 'todos',
    }))
  }, [filters.date])

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      positionName: 'todos',
    }))
  }, [filters.round])

  return {
    user,
    setAlerts,
    loading,
    setLoading,
    filters,
    setFilters,
    dataContext,
    setDataContext,
    candidacyHistory,
    dates,
    rounds,
    jobs,
    reportStatusPerJobOffer,
    reportStatusPerJobOfferTotal,
    statusStaff,
    colorsStatus,
    formContext,
  }
}
